import { callMethodOnKeydown, clamp, findSensorUnderCursor, screenCssToWorld, isTyping } from '../helpers'
import { state } from '../map_state/state'
import { viewport } from '../map_state/viewport'
import { Renderer } from '../renderer'
import { panTool } from './pan_tool'
import { addSensorTool } from './add_sensor_tool'
import { cropTool } from './crop_tool'
import { pointDistanceTool } from './point_distance_tool'
import { linearFlowTool } from './linear_flow_tool'
import { pathTracerTool } from './path_tracer_tool'
import { historyTool } from './history_tool'

class DragTool {
  constructor () {
    this.element = document.getElementById('mct-drag')
    this.sensorsTab = document.getElementById('mct-sensors-tab')
    this.groupsTab = document.getElementById('mct-groups-tab')
    this.groupButton = document.querySelector('.mct-group-mode')
    this.draggingCanvas = null
    this.draggingScope = null

    this.keys = {
      Escape: 'Cancel drag',
      D: 'Toggle drag mode'
    }

    this.bindEvents()
  }

  bindEvents () {
    this.activate = this.activate.bind(this)
    this.onKeyDown = this.onKeyDown.bind(this)

    this.onCanvasPointerDown = this.onCanvasPointerDown.bind(this)
    this.onCanvasPointerMove = this.onCanvasPointerMove.bind(this)
    this.onCanvasPointerUp = this.onCanvasPointerUp.bind(this)

    this.onSensorsTabPointerDown = this.onSensorsTabPointerDown.bind(this)
    this.onGroupsTabPointerDown = this.onGroupsTabPointerDown.bind(this)

    this.onDocumentPointerMove = this.onDocumentPointerMove.bind(this)
    this.onDocumentPointerUp = this.onDocumentPointerUp.bind(this)

    this.element.addEventListener('click', this.activate)
    document.addEventListener('keydown', this.onKeyDown)
  }

  unBindEvents () {
    this.deactivate(false)
    this.element.removeEventListener('click', this.activate)
    document.removeEventListener('keydown', this.onKeyDown)
  }

  onKeyDown (event) {
    if (isTyping(event.target)) return
    if (event.key.toLowerCase() === 'd') {
      callMethodOnKeydown(event.target, this.activate)
      return
    }

    if (event.key === 'Escape') {
      this.cancelAllDragging()
    }
  }

  activate (mode = true) {
    this.active = !state.dragMode && mode
    if (this.active) this.enable()
    else this.deactivate()
  }

  enable () {
    if (state.panMode) panTool.activate(false)
    else if (state.addSensorMode) addSensorTool.activate(false)
    else if (state.cropMode) cropTool.activate(false)
    else if (state.linearFlowMode) linearFlowTool.activate(false)
    else if (state.tracerMode) pathTracerTool.enableMode(state.tracerMode, false)
    if (state.pointDistanceMode) pointDistanceTool.activate(false)
    viewport.canvas.addEventListener('pointerdown', this.onCanvasPointerDown)
    viewport.canvas.addEventListener('pointermove', this.onCanvasPointerMove)
    viewport.canvas.addEventListener('pointerup', this.onCanvasPointerUp)
    viewport.canvas.addEventListener('pointercancel', this.onCanvasPointerUp)

    this.sensorsTab.addEventListener('pointerdown', this.onSensorsTabPointerDown)
    this.groupsTab.addEventListener('pointerdown', this.onGroupsTabPointerDown)

    document.addEventListener('pointermove', this.onDocumentPointerMove)
    document.addEventListener('pointerup', this.onDocumentPointerUp)
    document.addEventListener('pointercancel', this.onDocumentPointerUp)
    state.dragMode = true
    viewport.container.style.cursor = 'grab'
    viewport.sensorsTab.classList.add('grab')
    viewport.groupsTab.classList.add('grab')
    this.element.classList.add('selected')
    Renderer.render()
  }

  deactivate () {
    viewport.canvas.removeEventListener('pointerdown', this.onCanvasPointerDown)
    viewport.canvas.removeEventListener('pointermove', this.onCanvasPointerMove)
    viewport.canvas.removeEventListener('pointerup', this.onCanvasPointerUp)
    viewport.canvas.removeEventListener('pointercancel', this.onCanvasPointerUp)

    this.sensorsTab.removeEventListener('pointerdown', this.onSensorsTabPointerDown)
    this.groupsTab.removeEventListener('pointerdown', this.onGroupsTabPointerDown)

    document.removeEventListener('pointermove', this.onDocumentPointerMove)
    document.removeEventListener('pointerup', this.onDocumentPointerUp)
    document.removeEventListener('pointercancel', this.onDocumentPointerUp)

    state.dragMode = false
    this.element.classList.remove('selected')
    viewport.container.style.cursor = 'default'
    viewport.sensorsTab.classList.remove('grab')
    viewport.groupsTab.classList.remove('grab')

    this.cancelAllDragging(false)
    Renderer.render()
  }

  syncUi () {
    state.normalizeGroupState()
    this.element.classList.toggle('selected', state.dragMode)
    this.groupButton.classList.toggle('selected', state.groupMode)
    this.groupsTab.classList.toggle('show', state.groupMode)
    this.renderGroupsTab()
  }

  renderGroupsTab () {
    if (!this.groupsTab) return
    this.groupsTab.innerHTML = ''

    if (!state.groupMode) return

    state.groupSensorIds.forEach(sensorId => {
      const sensor = state.getSensorById(sensorId)
      if (!sensor) return
      this.groupsTab.appendChild(this.buildLooseSensorScope(sensor))
    })

    state.groups.forEach(group => {
      this.groupsTab.appendChild(this.buildGroupBox(group))
    })

    if (!state.groupSensorIds.length && !state.groups.length) {
      const empty = document.createElement('div')
      empty.className = 'mct-group-empty-state'
      empty.innerHTML = `
                <div class="mct-group-empty-title">No groups yet</div>
                <div class="mct-group-empty-copy">Turn on drag mode and drop a sensor here to create a loose sensor scope or a group.</div>
            `
      this.groupsTab.appendChild(empty)
    }
  }

  buildLooseSensorScope (sensor) {
    const scope = document.createElement('div')
    scope.className = 'mct-group-loose-sensor'
    scope.dataset.sensorId = sensor.id
    scope.innerHTML = `
            <div class="mct-group-loose-main">
                <div class="mct-group-loose-name">${sensor.name}</div>
                <div class="mct-group-loose-label">Single sensor</div>
            </div>
            <button class="mct-group-scope-remove" type="button" aria-label="Remove sensor from groups tab">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1024 1024">
                    <path d="M512 64a448 448 0 1 1 0 896 448 448 0 0 1 0-896zM288 512a38.4 38.4 0 0 0 38.4 38.4h371.2a38.4 38.4 0 0 0 0-76.8H326.4A38.4 38.4 0 0 0 288 512z" fill="currentColor"></path>
                </svg>
            </button>
        `

    scope.querySelector('.mct-group-scope-remove')?.addEventListener('click', event => {
      event.stopPropagation()
      state.removeLooseSensorScope(sensor.id)
      Renderer.render()
    })

    return scope
  }

  buildGroupBox (group) {
    const wrapper = document.createElement('div')
    wrapper.className = 'mct-sensor-group-box'
    wrapper.dataset.groupId = group.id

    const chips = group.sensorIds.map(sensorId => {
      const sensor = state.getSensorById(sensorId)
      if (!sensor) return ''
      return `
                <div class="mct-group-sensor-chip" data-sensor-id="${sensor.id}" data-group-id="${group.id}">
                    <span class="mct-group-sensor-chip-name">${sensor.name}</span>
                    <button class="mct-group-sensor-chip-remove" type="button" data-sensor-id="${sensor.id}" data-group-id="${group.id}" aria-label="Remove sensor from group">
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1024 1024">
                            <path d="M512 64a448 448 0 1 1 0 896 448 448 0 0 1 0-896zM288 512a38.4 38.4 0 0 0 38.4 38.4h371.2a38.4 38.4 0 0 0 0-76.8H326.4A38.4 38.4 0 0 0 288 512z" fill="currentColor"></path>
                        </svg>
                    </button>
                </div>
            `
    }).join('')

    wrapper.innerHTML = `
            <div class="mct-sensor-group-box-header">
                <input class="mct-sensor-group-name" type="text" value="${group.name}" placeholder="Group name">
                <div class="mct-sensor-group-count">${group.sensorIds.length}</div>
            </div>
            <div class="mct-sensor-group-box-body">${chips}</div>
        `

    wrapper.querySelector('.mct-sensor-group-name')?.addEventListener('input', event => {
      group.name = event.target.value
      this.lastRenderSignature = ''
    })

    wrapper.querySelector('.mct-sensor-group-name')?.addEventListener('change', event => {
      historyTool.add()
    })

    wrapper.querySelectorAll('.mct-group-sensor-chip-remove').forEach(button => {
      button.addEventListener('click', event => {
        event.stopPropagation()
        state.removeSensorFromGroup(button.dataset.groupId, button.dataset.sensorId)
        Renderer.render()
      })
    })

    return wrapper
  }

  onSensorsTabPointerDown (event) {
    if (!state.dragMode) return
    if (event.target.closest('.mct-sensor-remove')) return

    const header = event.target.closest('.mct-sensor-header')
    const sensorElement = header?.closest('.mct-sensor')
    if (!sensorElement) return

    const sensor = state.getSensorById(sensorElement.dataset.sensorId)
    if (!sensor) return

    event.preventDefault()
    viewport.sensorsTab.classList.add('grabbing')
    viewport.groupsTab.classList.add('grabbing')
    this.startScopeDrag(event, sensor)
  }

  onGroupsTabPointerDown (event) {
    if (!state.dragMode) return
    if (event.target.closest('input, button, textarea, select')) return

    const chip = event.target.closest('.mct-group-sensor-chip, .mct-group-loose-sensor')
    if (!chip) return

    const sensor = state.getSensorById(chip.dataset.sensorId)
    if (!sensor) return

    event.preventDefault()
    viewport.sensorsTab.classList.add('grabbing')
    viewport.groupsTab.classList.add('grabbing')
    this.startScopeDrag(event, sensor)
  }

  startScopeDrag (event, sensor) {
    this.cancelScopeDragging()

    this.draggingScope = {
      sensorId: Number(sensor.id),
      pointerId: event.pointerId,
      ghost: this.buildDragGhost(sensor.name)
    }

    viewport.mct.appendChild(this.draggingScope.ghost)
    this.positionGhost(event.clientX, event.clientY)
  }

  onCanvasPointerDown (event) {
    if (!state.dragMode || !state.mapImage) return

    const [left, top] = viewport.getCanvasPosition()
    const world = screenCssToWorld({
      x: event.clientX - left,
      y: event.clientY - top
    })

    const sensor = findSensorUnderCursor(world)
    if (!sensor) return

    event.preventDefault()
    event.stopPropagation()

    state.selectedSensorId = sensor.id
    viewport.container.style.cursor = 'grabbing'
    this.draggingCanvas = {
      sensorId: sensor.id,
      pointerId: event.pointerId,
      offsetX: world.x - sensor.position.x,
      offsetY: world.y - sensor.position.y
    }

    viewport.canvas.setPointerCapture(event.pointerId)
    Renderer.render()
  }

  onCanvasPointerMove (event) {
    if (!this.draggingCanvas) return

    const sensor = state.getSensorById(this.draggingCanvas.sensorId)
    if (!sensor) {
      this.cancelCanvasDragging(event)
      return
    }

    const [left, top] = viewport.getCanvasPosition()
    const world = screenCssToWorld({
      x: event.clientX - left,
      y: event.clientY - top
    })

    const w = state.mapImage.naturalWidth
    const h = state.mapImage.naturalHeight

    sensor.position = {
      x: clamp(world.x - this.draggingCanvas.offsetX, state.mapWorld.x, state.mapWorld.x + w),
      y: clamp(world.y - this.draggingCanvas.offsetY, state.mapWorld.y, state.mapWorld.y + h)
    }

    Renderer.render()
  }

  onCanvasPointerUp (event) {
    viewport.container.style.cursor = 'grab'
    historyTool.add()
    this.cancelCanvasDragging(event)
  }

  cancelCanvasDragging (event) {
    if (!this.draggingCanvas) return
    if (event?.pointerId != null) viewport.canvas.releasePointerCapture(event.pointerId)

    this.draggingCanvas = null
    Renderer.render()
  }

  onDocumentPointerMove (event) {
    if (!this.draggingScope) return
    this.positionGhost(event.clientX, event.clientY)
    this.updateDropTarget(event)
  }

  onDocumentPointerUp (event) {
    if (!this.draggingScope) return
    this.applyScopeDrop(event)
    viewport.sensorsTab.classList.remove('grabbing')
    viewport.groupsTab.classList.remove('grabbing')
    this.cancelScopeDragging()
    historyTool.add()
    Renderer.render()
  }

  applyScopeDrop (event) {
    if (!state.groupMode) return

    const groupsPanel = event.target.closest('#mct-groups-tab')
    if (!groupsPanel) return

    const sensorId = this.draggingScope.sensorId
    const targetGroup = event.target.closest('.mct-sensor-group-box')
    const targetLoose = event.target.closest('.mct-group-loose-sensor')

    if (targetGroup?.dataset.groupId) {
      state.addSensorToGroup(targetGroup.dataset.groupId, sensorId)
      return
    }

    if (targetLoose?.dataset.sensorId) {
      const targetSensorId = Number(targetLoose.dataset.sensorId)
      if (targetSensorId === sensorId) return
      state.convertLooseSensorToGroup(targetSensorId, sensorId)
      return
    }

    state.addLooseSensorScope(sensorId)
  }

  updateDropTarget (event) {
    this.clearDropTarget()
    if (!state.groupMode) return

    const targetGroup = event.target.closest('.mct-sensor-group-box')
    const targetLoose = event.target.closest('.mct-group-loose-sensor')
    const groupsPanel = event.target.closest('#mct-groups-tab')

    if (targetGroup) {
      targetGroup.classList.add('mct-group-drop-target')
      return
    }

    if (targetLoose) {
      targetLoose.classList.add('mct-group-drop-target')
      return
    }

    if (groupsPanel) {
      groupsPanel.classList.add('mct-group-drop-target')
    }
  }

  clearDropTarget () {
    document.querySelectorAll('.mct-group-drop-target').forEach(element => {
      element.classList.remove('mct-group-drop-target')
    })
  }

  buildDragGhost (label) {
    const ghost = document.createElement('div')
    ghost.className = 'mct-drag-ghost'
    ghost.textContent = label
    return ghost
  }

  positionGhost (clientX, clientY) {
    if (!this.draggingScope?.ghost) return
    this.draggingScope.ghost.style.left = `${clientX + 14}px`
    this.draggingScope.ghost.style.top = `${clientY + 14}px`
  }

  cancelScopeDragging () {
    this.draggingScope?.ghost?.remove()
    this.draggingScope = null
    this.clearDropTarget()
  }

  cancelAllDragging (shouldRender = true) {
    this.cancelScopeDragging()
    this.cancelCanvasDragging()
    if (shouldRender) Renderer.render()
  }
}

export let dragTool = null

export function initDragTool () {
  dragTool = new DragTool()
  return dragTool
}
